import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import caseVideo from "@/features/missionkit/briefcase-opening.mp4";
import paperVideo from "@/features/missionkit/paper-open.mp4";
import paperCloseVideo from "@/features/missionkit/paper-close.mp4";
import glassesVideo from "@/features/missionkit/glasses-open.mp4";
import glassesCloseVideo from "@/features/missionkit/glasses-close.mp4";
import {
  useMissionBriefing,
  useMissionVideoUrl,
} from "@/features/missionkit/api/use-mission-media";

/**
 * Wall-clock lengths of the kit's own clips, in seconds — measured off the
 * files rather than guessed. Every timer below divides by the rate instead of
 * hardcoding the result, so re-cutting a clip or changing the rate cannot
 * leave a timer pointing at a number that used to be right.
 */
const CASE_SECONDS = 8;
const PAPER_SECONDS = 6.625;

/**
 * Above 1 the kit runs faster than it was shot.
 *
 * The mission's own clip is deliberately excluded: that one is content, not
 * furniture, and speeding it up would change what the operative is being told.
 */
const PLAYBACK_RATE = 1.4;

/** Fallback for a blocked `onEnded`, not the path this normally takes. */
const VIDEO_MS = Math.round((CASE_SECONDS / PLAYBACK_RATE) * 1000);

/**
 * The items struck off the loadout, by where they sit in the case.
 *
 * `left`/`top` are the centre of the X — the mark is centre-anchored — as
 * percentages of the video stage, not of the viewport. The marks live inside
 * the same box as the clip, so they hold their place at any screen size.
 * Re-measure these if the case art is ever re-shot; they are anchored to the
 * video's own composition and nothing computes them.
 *
 * The case now holds three items, and only the radio is struck off. The paper
 * and the glasses are deliberately absent: both can be picked up, and a
 * struck-off item that answers a tap reads as a mistake.
 */
const CROSSED_OUT = [{ label: "Radio", left: "74%", top: "55%" }];

/**
 * The paper's box in the case, in the same stage percentages as CROSSED_OUT —
 * measured off the render, not derived. The paper and the glasses are the two
 * things in the kit that answer a tap; the radio is struck off and inert.
 */
const PAPER_HOTSPOT = { left: "9%", top: "46%", width: "38%", height: "22%" };

// The glasses lie across the top of the case, above the paper. The two boxes
// are split at 46% so they never overlap — the glasses button renders after
// the paper's, so any overlap would be the glasses stealing the paper's taps.
const GLASSES_HOTSPOT = {
  left: "23%",
  top: "34%",
  width: "54%",
  height: "12%",
};

/**
 * Where the sheet ends up once it is held to camera, measured off the clip's
 * last frame. The text is inset from that so it sits ON the paper rather than
 * bleeding over the hands holding it.
 */
const PAPER_SHEET = { left: "16%", top: "17%", width: "68%", height: "64%" };

// The text waits for the end of the clip: the hands are still settling until
// then, and copy that arrives mid-motion reads as an overlay rather than as
// something written on the page.
const PAPER_TEXT_MS = Math.round((PAPER_SECONDS / PLAYBACK_RATE) * 1000);

/**
 * How long the reversed clip holds the sheet flat to camera before the fold
 * starts — measured off its frames, where the bend is first visible at ~3.3s.
 *
 * Everything the writing does on the way out has to finish inside this, or the
 * page folds with text still on it. `paper-timing.test.ts` holds it to that.
 */
const PAPER_FLAT_SECONDS = 3.2;
export const PAPER_FLAT_MS = Math.round(
  (PAPER_FLAT_SECONDS / PLAYBACK_RATE) * 1000,
);

/** A beat to read the last line by, before the page starts giving it up. */
export const PAPER_TEXT_HOLD_MS = 300;

/**
 * The beam traces the briefing in reading order, so the delay is the
 * character's position through the text rather than a hash: a laser cuts in
 * sequence, and scattering the order reads as a fade instead. The sweep is a
 * fixed span, so a long briefing etches faster per character rather than
 * taking proportionally longer.
 */
const ETCH_SWEEP_MS = 1500;
const ETCH_MS = 420;

/** Going the other way it is unpicked, not traced, so this one is scattered. */
export const DRIFT_SPREAD_MS = 500;
export const DRIFT_MS = 650;

/**
 * A character's moment inside the drift window — hashed from its position, not
 * drawn from Math.random().
 *
 * It has to be pure: this runs during render, and a fresh random on a
 * re-render would make characters that had already gone jump back and start
 * again. Hashing the index is stable for the life of the text and, at this
 * scale, indistinguishable from random.
 */
function driftDelay(index: number) {
  const noise = Math.sin((index + 1) * 12.9898) * 43758.5453;
  return Math.round((noise - Math.floor(noise)) * DRIFT_SPREAD_MS);
}

/**
 * The handover out of the glasses: the last frame ripples and blurs for a beat
 * before the mission's own clip takes over, so the cut reads as the lenses
 * pulling focus rather than as one video being swapped for another.
 */
const WARP_MS = 700;

/**
 * Peak strength of the ripple, tuned rather than picked: displacement much
 * past 26, or a frequency past ~0.02, stops reading as a ripple and starts
 * reading as a decode artifact — the frame shreds into noise instead of
 * bending. Low frequency is what makes it a wave rather than static.
 */
const WARP_BASE_FREQUENCY = 0.004;
const WARP_PEAK_FREQUENCY = 0.008;
const WARP_PEAK_SCALE = 26;
const WARP_PEAK_BLUR = 4;

/**
 * The ripple, driven from inside the filter rather than from CSS.
 *
 * CSS can animate `filter: blur()` but not the innards of an SVG filter, and a
 * displacement map is the only thing that actually warps pixels rather than
 * smearing them. SMIL runs it, and the blur rides along in the same filter so
 * both ramp on one clock — a CSS transition could not have interpolated the
 * `url()` half anyway. The element mounts only for the length of the handover,
 * so the animation starts on mount and never needs restarting.
 *
 * The peak values are tuned, not picked: displacement much past `scale` 26 or
 * a `baseFrequency` past ~0.02 stops reading as a ripple and starts reading as
 * a decode artifact — the frame shreds into noise rather than bending. Low
 * frequency is what makes it a wave instead of static.
 *
 * It runs over the glasses clip's final frame, which is parked and decoding
 * nothing by then. Displacement over a still costs a fraction of what it would
 * over a playing video, which matters on a phone already holding two decoders.
 */
function WarpFilter() {
  const turbulence = useRef<SVGFETurbulenceElement>(null);
  const displacement = useRef<SVGFEDisplacementMapElement>(null);
  const blur = useRef<SVGFEGaussianBlurElement>(null);

  // Driven from rAF rather than SMIL. SMIL expresses this in four lines of
  // markup and no script, but it does not reliably advance its clock here —
  // it renders one frame and freezes, leaving the ripple stuck at its opening
  // value, which looks like a smudge rather than a warp. rAF is a few lines
  // more and actually runs.
  useEffect(() => {
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / WARP_MS);
      // One half-sine: in from nothing, out to nothing, peaking mid-handover.
      const ease = Math.sin(Math.PI * t);
      turbulence.current?.setAttribute(
        "baseFrequency",
        String(WARP_BASE_FREQUENCY + WARP_PEAK_FREQUENCY * ease),
      );
      displacement.current?.setAttribute(
        "scale",
        String(WARP_PEAK_SCALE * ease),
      );
      blur.current?.setAttribute("stdDeviation", String(WARP_PEAK_BLUR * ease));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute h-px w-px opacity-0"
    >
      <filter id="sm-warp" x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence
          ref={turbulence}
          type="fractalNoise"
          numOctaves="2"
          seed="7"
          baseFrequency={WARP_BASE_FREQUENCY}
          result="noise"
        />
        <feDisplacementMap
          ref={displacement}
          in="SourceGraphic"
          in2="noise"
          scale="0"
          xChannelSelector="R"
          yChannelSelector="G"
          result="warped"
        />
        <feGaussianBlur ref={blur} in="warped" stdDeviation="0" />
      </filter>
    </svg>
  );
}

// `scale`/`translate`, not `transform`: Tailwind v4's utilities set those
// standalone properties, so a transform transition would never fire.
const STRIKE_IN_CLASS =
  "[transition:opacity_.45s_ease,scale_.45s_cubic-bezier(.2,1.4,.4,1)] " +
  "delay-[var(--sm-strike-delay)]";

/** One hand-drawn-ish X struck over an item in the case. */
function CrossOut({
  label,
  left,
  top,
  delayMs,
  shown,
}: {
  label: string;
  left: string;
  top: string;
  delayMs: number;
  shown: boolean;
}) {
  return (
    <div
      role="img"
      aria-label={`${label} — not issued`}
      style={
        {
          left,
          top,
          "--sm-strike-delay": `${shown ? delayMs : 0}ms`,
        } as CSSProperties
      }
      className={`pointer-events-none absolute w-[30%] -translate-x-1/2 -translate-y-1/2 ${STRIKE_IN_CLASS} ${
        shown ? "scale-100 opacity-100" : "scale-[1.6] opacity-0"
      }`}
    >
      <svg
        viewBox="0 0 100 100"
        className="block w-full [filter:drop-shadow(0_2px_10px_rgba(0,0,0,.8))]"
      >
        <g
          stroke="#8c1512"
          strokeWidth="11"
          strokeLinecap="round"
          // Endpoints are deliberately not symmetric — the two strokes miss
          // each other slightly, so it reads as struck by hand rather than
          // stamped by a rendering engine.
          fill="none"
        >
          <line x1="12" y1="10" x2="88" y2="90" />
          <line x1="88" y1="14" x2="10" y2="88" />
        </g>
      </svg>
    </div>
  );
}

/**
 * The briefcase-opening kit: the case opens and the radio is struck off,
 * leaving the paper and the glasses.
 *
 * It shows no mission text. The brief arrives by SMS from the handler, and a
 * second copy on this page is one that can disagree with it — so the page is
 * the same whether or not a mission is loaded. `/missionkit/code/<code>/<id>`
 * still resolves the mission, to fail loudly on a bad link.
 */
export function MissionKit({ missionId }: { missionId?: string | number }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const paperRef = useRef<HTMLVideoElement>(null);
  const closeRef = useRef<HTMLVideoElement>(null);
  const [opened, setOpened] = useState(false);
  const [paperOpened, setPaperOpened] = useState(false);
  const [textInked, setTextInked] = useState(false);
  const [paperSettled, setPaperSettled] = useState(false);
  const [paperClosing, setPaperClosing] = useState(false);
  // The glasses run open -> the mission's clip -> reverse, with no Done in the
  // middle: the clip ending IS the cue to take them off, so asking for a click
  // would only add a step to a sequence that has already finished itself.
  // Kept as its own small set of states rather than an abstraction shared with
  // the paper: the paper waits on a human reading, these wait on a clip.
  const glassesRef = useRef<HTMLVideoElement>(null);
  const glassesCloseRef = useRef<HTMLVideoElement>(null);
  const [glassesOpened, setGlassesOpened] = useState(false);
  const [glassesClosing, setGlassesClosing] = useState(false);
  const [glassesSettled, setGlassesSettled] = useState(false);
  // The beat between the glasses landing and the mission clip taking over.
  // Its own state rather than a flag on `glassesSettled`, because everything
  // downstream keys off settled meaning "the mission clip may now play" — the
  // warp has to finish before that becomes true.
  const [warping, setWarping] = useState(false);
  // What the operative sees once the glasses are on: the mission's own clip,
  // staged per mission rather than bundled. Missions with nothing staged skip
  // it and the glasses close as they always did.
  const missionClipRef = useRef<HTMLVideoElement>(null);
  // Only for a clip that refuses to load. The glasses take themselves off when
  // the clip ends, so a clip that never ends needs the manual way out back.
  const [missionClipFailed, setMissionClipFailed] = useState(false);
  const missionVideoUrl = useMissionVideoUrl(missionId);
  const { data: briefing = "" } = useMissionBriefing(missionId);

  // Split to words first, then to characters within each word.
  //
  // Every character is its own inline-block so it can be stamped in
  // separately, and the browser will happily break a line between any two of
  // them — which split words down the middle ("the fragil / e ones"). Keeping
  // each word in a nowrap box of its own puts the break back between words,
  // where it belongs. Newlines stay bare: a box around one stops the line
  // breaking at all.
  const etchedWords = useMemo(() => {
    const total = [...briefing].length || 1;
    let index = 0;
    return briefing.split(/(\s)/).map((token) => ({
      token,
      characters: [...token].map((value) => {
        const position = index++;
        return {
          value,
          etchDelay: Math.round((position / total) * ETCH_SWEEP_MS),
          driftDelay: driftDelay(position),
        };
      }),
    }));
  }, [briefing]);

  // Force muted (React doesn't reliably set the property) so autoplay isn't
  // blocked and the case doesn't sit on a black frame. playbackRate has to be
  // set on the element too — there is no attribute for it.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playbackRate = PLAYBACK_RATE;
    v.play().catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setOpened(true), VIDEO_MS);
    return () => clearTimeout(t);
  }, []);

  // The paper clip is mounted only once it is wanted, so it does not download
  // for a visitor who never reaches for it. That means the element does not
  // exist when the click happens — play it on mount instead, where autoplay is
  // allowed because the mount was caused by a real tap.
  //
  // Which element this picks matters more than it looks. `play()` on a video
  // that has already ended seeks it back to zero and plays it again, so
  // reaching for the open clip here while closing would run a second full
  // decode underneath the reverse. Two clips decoding at once plus the case
  // is enough to evict a surface from a real decoder pool, and the evicted
  // layer paints black — the flash. Select the clip this state actually wants.
  useEffect(() => {
    const v = paperClosing ? closeRef.current : paperRef.current;
    if (!paperOpened || !v) return;
    v.muted = true;
    v.playbackRate = PLAYBACK_RATE;
    v.play().catch(() => {});
  }, [paperOpened, paperClosing]);

  useEffect(() => {
    if (!paperOpened) return;
    const t = setTimeout(
      () => setTextInked(!paperClosing),
      paperClosing ? PAPER_TEXT_HOLD_MS : PAPER_TEXT_MS,
    );
    return () => clearTimeout(t);
  }, [paperOpened, paperClosing]);

  useEffect(() => {
    if (!warping) return;
    const t = setTimeout(() => {
      setWarping(false);
      setGlassesSettled(true);
    }, WARP_MS);
    return () => clearTimeout(t);
  }, [warping]);

  useEffect(() => {
    const v = glassesClosing ? glassesCloseRef.current : glassesRef.current;
    if (!glassesOpened || !v) return;
    v.muted = true;
    v.playbackRate = PLAYBACK_RATE;
    v.play().catch(() => {});
  }, [glassesOpened, glassesClosing]);

  // The mission's clip takes over the moment the glasses finish going on. It
  // is mounted earlier than that, so it downloads and decodes its first frame
  // while the glasses are still moving and the handover has something to show.
  useEffect(() => {
    const v = missionClipRef.current;
    if (!glassesSettled || glassesClosing || !v) return;
    v.muted = true;
    v.play().catch(() => {});
  }, [glassesSettled, glassesClosing]);

  /**
   * Which of the four things a character is doing right now.
   *
   * The etch and the drift are animations rather than transitions: both ramp
   * through a hot middle and back out, and a transition only has two ends.
   * `both` fill keeps each one's last frame, so dropping the animation on the
   * held state lands on exactly what the etch finished on — no jump.
   */
  const characterStyle = (char: {
    etchDelay: number;
    driftDelay: number;
  }): CSSProperties => {
    if (paperClosing) {
      return textInked
        ? {}
        : {
            animation: `paper-drift ${DRIFT_MS}ms ease-in ${char.driftDelay}ms both`,
          };
    }
    return textInked
      ? {
          animation: `paper-etch ${ETCH_MS}ms ease-out ${char.etchDelay}ms both`,
        }
      : { opacity: 0 };
  };

  return (
    <section className="relative flex h-[100dvh] items-center justify-center overflow-hidden bg-[#050403] font-sans">
      {/* No title banner — the case speaks for itself. The mission's name and
          level went with it; the dossier below still carries the brief. */}

      {/* portrait stage: the case fills the page height, sides crop */}
      <div className="relative aspect-[720/1280] w-[calc(100dvh*720/1280)]">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={() => setOpened(true)}
          className="block h-full w-full object-cover"
        >
          <source src={caseVideo} type="video/mp4" />
        </video>

        {/* what they are NOT issued — struck off once the case has opened */}
        {CROSSED_OUT.map((item, i) => (
          <CrossOut
            key={item.label}
            label={item.label}
            left={item.left}
            top={item.top}
            delayMs={200 + i * 220}
            shown={opened}
          />
        ))}

        {/* The paper is the one thing they were issued, so it is the one thing
            that answers a tap. A real button, not a div: this is the only
            interaction on the page and it has to be reachable by keyboard.
            Only after the case has finished opening — before that the paper is
            still moving and the hit box would not be over it. */}
        {opened && !paperOpened && (
          <button
            type="button"
            aria-label="Open the paper"
            onClick={() => setPaperOpened(true)}
            style={PAPER_HOTSPOT as CSSProperties}
            className="absolute cursor-pointer rounded-lg border-none bg-transparent focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#d6a94e]"
          />
        )}

        {/* Covers the case entirely rather than swapping the source, so the
            first frame of the paper clip replaces the last frame of the case
            with no flash of black in between. */}
        {paperOpened && (
          <video
            ref={paperRef}
            muted
            playsInline
            preload="auto"
            onEnded={() => setPaperSettled(true)}
            className={`absolute inset-0 z-[5] block h-full w-full object-cover ${
              paperClosing ? "opacity-0" : "opacity-100"
            }`}
          >
            <source src={paperVideo} type="video/mp4" />
          </video>
        )}

        {/* The reverse is its own element laid over the first, never a source
            swap on the same one. Swapping made React remount the video, and a
            fresh element paints nothing until its first frame decodes — which
            is the flash. This one stays invisible until it is genuinely
            playing, and the opened clip sits paused on its last frame
            underneath, so the gap shows the paper rather than a black hole.
            The two frames are identical, so the handover is invisible. */}
        {paperOpened && (
          <video
            ref={closeRef}
            muted
            playsInline
            preload="auto"
            onEnded={() => {
              // Back to the open case, exactly as it was before the tap.
              setPaperClosing(false);
              setPaperOpened(false);
              setPaperSettled(false);
            }}
            className="absolute inset-0 z-[4] block h-full w-full object-cover"
          >
            <source src={paperCloseVideo} type="video/mp4" />
          </video>
        )}

        {opened && !paperOpened && !glassesOpened && (
          <button
            type="button"
            aria-label="Pick up the glasses"
            onClick={() => setGlassesOpened(true)}
            style={GLASSES_HOTSPOT as CSSProperties}
            className="absolute cursor-pointer rounded-lg border-none bg-transparent focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#d6a94e]"
          />
        )}

        {glassesOpened && (
          <>
            {warping && <WarpFilter />}
            <video
              ref={glassesRef}
              muted
              playsInline
              preload="auto"
              onEnded={() => setWarping(true)}
              className={`absolute inset-0 z-[5] block h-full w-full object-cover ${
                glassesClosing ? "opacity-0" : "opacity-100"
              } ${
                // `motion-reduce` drops the ripple to a plain cut: a full-frame
                // warp is exactly the kind of motion that setting is for.
                warping
                  ? "[filter:url(#sm-warp)] motion-reduce:[filter:none]"
                  : ""
              }`}
            >
              <source src={glassesVideo} type="video/mp4" />
            </video>
            <video
              ref={glassesCloseRef}
              muted
              playsInline
              preload="auto"
              onEnded={() => {
                setGlassesClosing(false);
                setGlassesOpened(false);
                setGlassesSettled(false);
                setWarping(false);
                setMissionClipFailed(false);
              }}
              className="absolute inset-0 z-[4] block h-full w-full object-cover"
            >
              <source src={glassesCloseVideo} type="video/mp4" />
            </video>

            {/* Over the glasses clip, which stays paused on its last frame
                underneath. Hidden rather than unmounted while it waits, so it
                is already decoded when the glasses settle; hidden again on the
                way out, so the reverse plays against the glasses and not
                against the last frame of this one. */}
            {missionVideoUrl && (
              <video
                ref={missionClipRef}
                muted
                playsInline
                preload="auto"
                // The clip finishing is the whole cue: straight into the
                // reverse, no button in between.
                onEnded={() => setGlassesClosing(true)}
                // A clip that will not load must not strand the operative —
                // nothing would ever end, so nothing would ever start the
                // reverse. Hand back the Done button instead.
                onError={() => setMissionClipFailed(true)}
                className={`absolute inset-0 z-[6] block h-full w-full object-cover ${
                  glassesSettled && !glassesClosing
                    ? "opacity-100"
                    : "opacity-0"
                }`}
              >
                <source src={missionVideoUrl} type="video/mp4" />
              </video>
            )}
          </>
        )}

        {/* Only for missions with no clip staged, or one that would not load.
            When there is a clip, it takes the glasses off itself. */}
        {glassesSettled &&
          !glassesClosing &&
          (!missionVideoUrl || missionClipFailed) && (
            <button
              type="button"
              onClick={() => setGlassesClosing(true)}
              className="absolute bottom-[6%] left-1/2 z-[7] -translate-x-1/2 cursor-pointer rounded-lg border border-[#d6a94e80] bg-[rgb(10_8_7/0.72)] px-6 py-2.5 font-stencil text-[13px] tracking-[2px] text-[#e6d3a4] uppercase backdrop-blur-[2px]"
            >
              Done
            </button>
          )}

        {/* the briefing, stamped onto the sheet the hands are holding */}
        {paperOpened && briefing && (
          <div
            style={
              {
                ...PAPER_SHEET,
                // The keyframes cool to this, so the sheet's colour and the
                // etch's last frame cannot drift apart.
                "--paper-ink": "#120c04",
              } as CSSProperties
            }
            className="pointer-events-none absolute z-[6] flex items-center overflow-hidden font-mono text-[clamp(7px,1.7vh,13px)] leading-[1.5] font-semibold whitespace-pre-wrap text-[var(--paper-ink)]"
          >
            {/* Centred in the sheet rather than pinned to the top, so a short
                briefing and a long one both sit in the middle of the page. */}
            <div>
              {etchedWords.map((word, w) =>
                /\s/.test(word.token) ? (
                  word.token
                ) : (
                  <span key={w} className="inline-block whitespace-nowrap">
                    {word.characters.map((char, i) => (
                      <span
                        key={i}
                        style={characterStyle(char)}
                        className="inline-block"
                      >
                        {char.value}
                      </span>
                    ))}
                  </span>
                ),
              )}
            </div>
          </div>
        )}
        {/* Putting the paper back is the operative's move, not a timeout —
            they decide when they have read it. The clip is the opening one
            rendered in reverse, because a browser will not play a video
            backwards: playbackRate refuses negative values, and scrubbing
            currentTime by hand stutters between keyframes. */}
        {textInked && paperSettled && !paperClosing && (
          <button
            type="button"
            onClick={() => {
              setPaperClosing(true);
            }}
            className="absolute bottom-[6%] left-1/2 z-[7] -translate-x-1/2 cursor-pointer rounded-lg border border-[#d6a94e80] bg-[rgb(10_8_7/0.72)] px-6 py-2.5 font-stencil text-[13px] tracking-[2px] text-[#e6d3a4] uppercase backdrop-blur-[2px]"
          >
            Done
          </button>
        )}
      </div>
    </section>
  );
}
